import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../../utils/api'
import DealDetailModal from './DealDetailModal'

// ---------------------------------------------------------------------------
// Warehouse Drop-off availability calendar ("Deliver in person", spec_20260805).
//
// One week visible at a time (Mon-Sun columns, hour-slot rows), navigable over
// the 28-day booking horizon. Every working day is open 09:00-17:00 by
// default; operators record EXCEPTIONS by clicking slots (or a whole day)
// closed. Bookings render inside their slot so "can I close Thursday 2-3?"
// answers itself — closing never cancels a booking (spec decision #10), the
// operator rebooks from the Collections board.
//
// The toggle round-trips `operator_closed` (the raw closure fact), NEVER the
// computed `open` flag — `open` also encodes weekends, bank holidays and
// started slots, which must not be persisted as closures.
//
// Cell vocabulary (operator feedback 2026-08-06): grey = not ours to toggle
// (past day, weekend, bank holiday, already-started hour); green = open;
// red = closed by an operator. Colours and spacing come from the house
// tokens in styles/index.css, not ad-hoc greys.
// ---------------------------------------------------------------------------

const MS_PER_DAY = 86400000
const HORIZON_DAYS = 28

const CELL_STYLES = {
  open: { background: 'rgba(59, 178, 115, 0.12)', border: '1px solid rgba(59, 178, 115, 0.35)', cursor: 'pointer' },
  closed: { background: 'rgba(229, 98, 94, 0.12)', border: '1px solid rgba(229, 98, 94, 0.35)', cursor: 'pointer' },
  unavailable: { background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', cursor: 'default', opacity: 0.55 },
}

function addDays(date, days) { return new Date(date.getTime() + days * MS_PER_DAY) }
function isoDate(date) { return date.toISOString().slice(0, 10) }

function startOfWeek(date) {
  const d = new Date(date)
  const day = d.getUTCDay()
  const diff = day === 0 ? 6 : day - 1 // Monday start
  d.setUTCDate(d.getUTCDate() - diff)
  d.setUTCHours(0, 0, 0, 0)
  return d
}

// "09:00:00" -> "9am", "13:00:00" -> "1pm"
function slotLabel(hms) {
  const hour = Number(hms.slice(0, 2))
  return hour < 12 ? `${hour}am` : hour === 12 ? '12pm' : `${hour - 12}pm`
}

/** Why a day is out of the operator's hands, or null when it's toggleable. */
function unavailableReason(iso, date, day, todayIso) {
  if (iso < todayIso) return 'Past'
  if (date.getUTCDay() === 0 || date.getUTCDay() === 6) return 'Weekend'
  if (!day) return null
  const anyOperatorClosure = day.operator_closed || day.slots.some(s => s.operator_closed)
  // A weekday with nothing open and no closure rows = bank holiday (or, for
  // today, every remaining slot has started — same treatment either way).
  if (!day.open && !anyOperatorClosure) return iso === todayIso ? 'Done for today' : 'Bank holiday'
  return null
}

function WarehouseDropOff() {
  const today = useMemo(() => { const d = new Date(); d.setUTCHours(0, 0, 0, 0); return d }, [])
  const todayIso = useMemo(() => isoDate(today), [today])
  const firstWeek = useMemo(() => startOfWeek(today), [today])
  const lastWeek = useMemo(() => startOfWeek(addDays(today, HORIZON_DAYS)), [today])

  const [weekStart, setWeekStart] = useState(firstWeek)
  const [days, setDays] = useState([])          // availability rows for the visible week
  const [bookings, setBookings] = useState([])
  const [contacts, setContacts] = useState({})  // quote_id -> { name, email }
  const [loading, setLoading] = useState(true)
  const [savingDay, setSavingDay] = useState(null)
  const [error, setError] = useState(null)
  const [openedDeal, setOpenedDeal] = useState(null)

  const load = useCallback(async (start) => {
    setLoading(true)
    setError(null)
    const from = isoDate(start)
    const to = isoDate(addDays(start, 6))
    try {
      const [avail, booked] = await Promise.all([
        api.get(`/api/deals/warehouse-dropoff/availability?from=${from}&to=${to}`),
        api.get(`/api/deals/warehouse-dropoff/bookings?from=${from}&to=${to}`),
      ])
      setDays(avail.days || [])
      setBookings(booked.bookings || [])
      const ids = [...new Set((booked.bookings || []).map(b => b.quote_id))]
      if (ids.length > 0) {
        // Names come from the board's existing batch-contacts machinery —
        // the Prism overlay is deliberately PII-free.
        api.post('/api/deals/collections/batch-contacts', { dealIds: ids })
          .then(r => setContacts(r.contacts || {}))
          .catch(() => setContacts({}))
      } else {
        setContacts({})
      }
    } catch (e) {
      setError(e.message || 'Failed to load availability')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(weekStart) }, [weekStart, load])

  const dayByIso = useMemo(() => new Map(days.map(d => [d.day, d])), [days])
  const slotStarts = useMemo(
    () => (days[0]?.slots || []).map(s => s.start),
    [days]
  )
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i)
    const iso = isoDate(date)
    const day = dayByIso.get(iso)
    return { date, iso, day, reason: unavailableReason(iso, date, day, todayIso) }
  }), [weekStart, dayByIso, todayIso])

  const bookingsFor = useCallback((iso, slotStart) => bookings.filter(b =>
    b.collection_date === iso && (b.dropoff_slot_start || null) === slotStart
  ), [bookings])

  const bookingCountForDay = useCallback(
    (iso) => bookings.filter(b => b.collection_date === iso).length,
    [bookings]
  )

  async function putDay(iso, body) {
    setSavingDay(iso)
    setError(null)
    try {
      const updated = await api.put(`/api/deals/warehouse-dropoff/days/${iso}`, body)
      setDays(prev => prev.map(d => (d.day === iso ? updated : d)))
    } catch (e) {
      setError(e.message || 'Failed to update the day')
    } finally {
      setSavingDay(null)
    }
  }

  function toggleSlot(day, slot) {
    const closedSlots = day.slots.filter(s => s.operator_closed).map(s => s.start)
    const closing = !slot.operator_closed
    if (closing) {
      const inSlot = bookingsFor(day.day, slot.start).length
      if (inSlot > 0 && !window.confirm(
        `${inSlot} booking${inSlot === 1 ? '' : 's'} in this slot. Closing does NOT cancel or move ` +
        'them — rebook from the Collections board and contact the customer. Close the slot anyway?'
      )) return
    }
    const next = closing
      ? [...closedSlots, slot.start]
      : closedSlots.filter(s => s !== slot.start)
    putDay(day.day, { closed: false, closed_slots: next })
  }

  function toggleWholeDay(day) {
    if (day.operator_closed) {
      putDay(day.day, { closed: false, closed_slots: [] })
      return
    }
    const count = bookingCountForDay(day.day)
    if (count > 0 && !window.confirm(
      `${count} booking${count === 1 ? '' : 's'} on this day. Closing does NOT cancel or move ` +
      'them — rebook from the Collections board and contact the customer. Close the whole day anyway?'
    )) return
    putDay(day.day, { closed: true })
  }

  function bookingChip(b) {
    const contact = contacts[b.quote_id] || {}
    const label = contact.name ? `${contact.name} · ${b.quote_reference_code}` : b.quote_reference_code
    return (
      <div key={b.quote_id} title={`${b.quote_reference_code}${contact.email ? ` · ${contact.email}` : ''} — click to open`}
        onClick={(e) => {
          // The chip sits inside the slot-toggle button: opening the deal
          // must not also toggle the slot.
          e.stopPropagation()
          setOpenedDeal({
            id: b.quote_id,
            quote_id: b.quote_id,
            reference_code: b.quote_reference_code,
            dealname: contact.name || b.quote_reference_code,
          })
        }}
        style={{
          fontSize: 10, padding: '2px 6px', borderRadius: 3, marginTop: 3,
          background: 'rgba(78, 128, 152, 0.15)', color: '#4E8098', fontWeight: 600,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          cursor: 'pointer', textDecoration: 'underline',
        }}>
        {label}
      </div>
    )
  }

  function dayHeader({ date, iso, day, reason }) {
    const isToday = iso === todayIso
    const muted = Boolean(reason)
    return (
      <div key={iso} style={{
        textAlign: 'center',
        padding: 'var(--space-2)',
        borderRadius: 'var(--radius)',
        background: isToday ? 'var(--color-bg-secondary)' : 'transparent',
        opacity: muted ? 0.55 : 1,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: muted ? 'var(--color-text-muted)' : 'inherit' }}>
          {date.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' })}
          {isToday && <span className="badge badge-info" style={{ marginLeft: 6, fontSize: 9 }}>Today</span>}
        </div>
        {reason ? (
          <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{reason}</span>
        ) : day && (
          <button className="btn btn-secondary btn-xs" style={{ fontSize: 10 }}
            disabled={savingDay === iso} onClick={() => toggleWholeDay(day)}>
            {day.operator_closed ? 'Reopen day' : 'Close day'}
          </button>
        )}
      </div>
    )
  }

  function slotCell({ iso, day, reason }, start) {
    const slot = day?.slots.find(s => s.start === start)
    const slotBookings = bookingsFor(iso, start)
    // Grey = not the operator's to toggle here: another force closed it
    // (past/weekend/holiday/started) or the whole day is closed above.
    const unavailable = Boolean(reason) || !slot || day.operator_closed
      || (!slot.open && !slot.operator_closed)
    const state = unavailable && !day?.operator_closed ? 'unavailable'
      : day?.operator_closed || slot?.operator_closed ? 'closed'
      : 'open'
    const toggleable = !unavailable && savingDay !== iso
    return (
      <button key={iso} disabled={!toggleable && !slotBookings.length}
        onClick={() => toggleable && slot && toggleSlot(day, slot)}
        title={state === 'closed' ? (day?.operator_closed ? 'Whole day closed — reopen from the day header' : 'Closed by an operator — click to reopen')
          : state === 'open' ? 'Open — click to close this slot'
          : 'Unavailable (past, weekend, bank holiday or already started)'}
        style={{
          minHeight: 44, padding: 4, borderRadius: 'var(--radius)', textAlign: 'left',
          font: 'inherit',
          ...CELL_STYLES[state],
          ...(toggleable ? {} : { cursor: 'default' }),
        }}>
        {state === 'closed' && !day?.operator_closed && (
          <span style={{ fontSize: 10, color: '#E5625E', fontWeight: 700 }}>CLOSED</span>
        )}
        {slotBookings.map(bookingChip)}
      </button>
    )
  }

  const canGoBack = weekStart > firstWeek
  const canGoForward = weekStart < lastWeek

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Warehouse Drop-off</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--color-text-muted)', fontSize: 13, maxWidth: 640 }}>
            "Deliver in person" availability — every working day is open 9am-5pm unless closed here.
            Click a slot to close/reopen it; use a day header to close the whole day. Closing never
            cancels a booking: rebook it from the Collections board, then tell the customer.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
          <button className="btn btn-secondary btn-xs" disabled={!canGoBack}
            onClick={() => setWeekStart(w => addDays(w, -7))}>← Prev week</button>
          <button className="btn btn-secondary btn-xs" disabled={!canGoForward}
            onClick={() => setWeekStart(w => addDays(w, 7))}>Next week →</button>
        </div>
      </div>

      {error && (
        <div className="card" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-3)', color: '#E5625E', fontSize: 13 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
          <div className="loading-spinner" />
        </div>
      ) : (
        <div className="card" style={{ padding: 'var(--space-3)', overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '76px repeat(7, 1fr)', gap: 6, minWidth: 820 }}>
            <div />
            {weekDays.map(dayHeader)}

            {/* "Anytime" row: bookings with no slot */}
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', alignSelf: 'center' }}>Anytime</div>
            {weekDays.map(({ iso, reason }) => {
              const anytime = bookingsFor(iso, null)
              return (
                <div key={iso} style={{
                  minHeight: 32, padding: 4, borderRadius: 'var(--radius)',
                  border: '1px dashed var(--color-border)',
                  opacity: reason ? 0.55 : 1,
                }}>
                  {anytime.map(bookingChip)}
                </div>
              )
            })}

            {/* Slot rows */}
            {slotStarts.map(start => (
              <React.Fragment key={start}>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)', alignSelf: 'center', whiteSpace: 'nowrap' }}>
                  {slotLabel(start)}–{slotLabel(`${String(Number(start.slice(0, 2)) + 1).padStart(2, '0')}:00:00`)}
                </div>
                {weekDays.map(wd => slotCell(wd, start))}
              </React.Fragment>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-3)', fontSize: 11, color: 'var(--color-text-muted)' }}>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, verticalAlign: -1, marginRight: 5, ...CELL_STYLES.open }} />Open — click to close</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, verticalAlign: -1, marginRight: 5, ...CELL_STYLES.closed }} />Closed by us — click to reopen</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, verticalAlign: -1, marginRight: 5, ...CELL_STYLES.unavailable }} />Unavailable (past / weekend / bank holiday)</span>
          </div>
        </div>
      )}

      {openedDeal && (
        <DealDetailModal
          deal={openedDeal}
          onClose={() => setOpenedDeal(null)}
          onDealUpdated={() => { setOpenedDeal(null); load(weekStart) }}
        />
      )}
    </div>
  )
}

export default WarehouseDropOff
